/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import { C11nEnv } from '../interpreter/c11n-env';
import { getResolvedContextTreeNodeProps } from '../utils/form-handler';

import Validator from './validator';

/**
 * Provides the basic APIs to handle validation in the Constellation infrastructure
 */

/**
  Object that contains APIs that handle validations on a field
 * @param pConnect The context object that is used for this instance of the ValidationApi
 */

class ValidationApi {
  private readonly c11nEnv: C11nEnv;

  constructor(pConnect: C11nEnv) {
    this.c11nEnv = pConnect;
  }

  /**
   * It Validates the field against the validation provided to the field and updates the state with the validation message.
   * If validation success it will return object with status as true
   * else it will return status as false along with an error message.
   * @param value - user provided value of the field to be validated.
   * @param propertyName - name of the filed to be validated. The default value is taken from redux state if propertyname is not provided.
   * @returns object contains status and message
   * @public
   * @example <caption> example of validate() validate the property with it's value.
   * </caption>
   * pConnect.getValidationApi().validate('mail.com', '.email');
   * //Output - when validation fails
   * {
   *    status : false,
   *    message: 'Invalid Email'
   * }
   *
   * pConnect.getValidationApi().validate('abc@gamil.com', '.email');
   * //Output - when validation success
   * {
   *    status : true
   * }
   */
  validate(value: string, propertyName?: string) {
    const { value: statePropsValue } = this.c11nEnv.getStateProps();

    if (!propertyName) {
      propertyName = statePropsValue;
      if (!propertyName) {
        const componentConfig = this.c11nEnv.getComponentConfig();
        const stateProps = this.c11nEnv.getStateProps();

        if (componentConfig.selectionMode === 'multi') {
          propertyName = stateProps.selectionList;
        } else if (componentConfig.renderMode === 'Editable') {
          propertyName = stateProps.referenceList;
        } else if (componentConfig.selectionMode === 'single') {
          propertyName = stateProps.selectionKey;
        } else {
          propertyName = stateProps.value;
        }
      }
    }

    const contextTreeNodeProps = getResolvedContextTreeNodeProps(
      this.c11nEnv.getContextName(),
      this.c11nEnv.getPageReference(),
      propertyName
    );

    const { message } = Validator.validate(
      this.c11nEnv.getComponentName()?.toLowerCase(),
      propertyName,
      value,
      contextTreeNodeProps
    );

    return message ? { status: false, message } : { status: true };
  }
}

export default ValidationApi;
